import React, { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";

export function AdminHeader({ title, subtitle, actions }) {
  return (
    <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-14 lg:top-0 z-20">
      <div className="px-4 sm:px-8 py-4 sm:py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-serif text-xl sm:text-2xl font-semibold text-npst-ink">{title}</h1>
          {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">{actions}</div>
      </div>
    </div>
  );
}

export function AdminPage({ children }) {
  return <div className="px-4 sm:px-8 py-6">{children}</div>;
}

function CountUp({ value }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const num = typeof value === "number" ? value : null;
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView || num === null) return undefined;
    const c = animate(0, num, { duration: 1.1, ease: [0.33, 1, 0.68, 1], onUpdate: (v) => setN(Math.round(v)) });
    return () => c.stop();
  }, [inView, num]);
  return <span ref={ref}>{num === null ? (value ?? "—") : n.toLocaleString("en-IN")}</span>;
}

export function StatCard({ label, value, accent }) {
  return (
    <div className="hover-lift relative overflow-hidden bg-white border border-slate-200 rounded-lg p-5" data-testid={`stat-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <span className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-npst-gold/0 via-npst-gold/60 to-npst-gold/0" aria-hidden="true" />
      <div className="label-eyebrow text-slate-400 mb-2">{label}</div>
      <div className={`font-serif text-4xl font-semibold ${accent || "text-npst-navy"}`}><CountUp value={value} /></div>
    </div>
  );
}

const STATE_STYLES = {
  published: "bg-emerald-50 text-emerald-700 border-emerald-200",
  draft: "bg-slate-100 text-slate-600 border-slate-200",
  under_review: "bg-amber-50 text-amber-700 border-amber-200",
  archived: "bg-slate-100 text-slate-500 border-slate-200",
  scheduled: "bg-blue-50 text-blue-700 border-blue-200",
  new: "bg-blue-50 text-blue-700 border-blue-200",
  read: "bg-slate-100 text-slate-500 border-slate-200",
};

export function StateBadge({ state }) {
  const cls = STATE_STYLES[state] || "bg-slate-100 text-slate-600 border-slate-200";
  return <span className={`inline-block text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${cls}`}>{(state || "").replace("_", " ")}</span>;
}
